import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Grid,
  Card,
  CardContent,
  Box,
  Typography,
  IconButton,
  Tooltip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import BookmarkAddedIcon from '@mui/icons-material/BookmarkAdded';
import DoNotDisturbOnIcon from '@mui/icons-material/DoNotDisturbOn';
import { getSecureImageUrl, handleImageError } from '../../utils/imageUtils';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';

/**
 * Generic large view component for collection/wishlist items
 * @param {Object} props - Component props
 * @param {Array} props.items - Array of items to display
 * @param {function} props.onEdit - Function to handle edit button click
 * @param {function} props.onDelete - Function to handle delete button click
 * @param {string} props.itemType - Type of item ('game', 'console', 'lego')
 * @param {boolean} props.isWishlist - Whether this is a wishlist view
 * @param {function} props.onMoveToCollection - Function to handle moving item to collection (for wishlists)
 * @param {Object} props.renderProps - Custom render props for item-specific fields
 * @returns {JSX.Element} Large view component
 */
const LargeView = ({
  items,
  onEdit,
  onDelete,
  itemType = 'item',
  isWishlist = false,
  onMoveToCollection,
  onToggleBacklog,
  onToggleDNF,
  renderProps = {}
}) => {
  const navigate = useNavigate();
  const { featureFlags } = useFeatureFlags();
  // Default render functions
  const defaultRenderProps = {
    getImageUrl: (item) => {
      if (itemType === 'console') {
        return item.platformLogo || item.imageUrl;
      }
      return item.imageUrl || item.set_img_url;
    },
    getItemName: (item) => item.name,
    getItemSubtitle: (item) => {
      if (itemType === 'game') return `Platform: ${item.platform}`;
      if (itemType === 'console') return `Generation: ${item.generation || 'N/A'}`;
      if (itemType === 'lego') {
        const setNum = item.setNum || item.set_num;
        return `Set #: ${setNum ? setNum.split('-')[0] : 'N/A'}`;
      }
      if (itemType === 'movie') return `Format: ${item.format || 'N/A'}`;
      if (itemType === 'music') return `Artist: ${item.artist || 'N/A'}`;
      if (itemType === 'book') return `Author: ${item.author || 'N/A'}`;
      if (itemType === 'board-game') {
        const players = item.minPlayers && item.maxPlayers 
          ? (item.minPlayers === item.maxPlayers ? `${item.minPlayers} players` : `${item.minPlayers}-${item.maxPlayers} players`)
          : 'N/A';
        return `Players: ${players}`;
      }
      return '';
    },
    getItemDetailUrl: (item) => {
      if (itemType === 'game') return `/item/game/${item._id}`;
      if (itemType === 'console') return `/item/console/${item._id}`;
      if (itemType === 'lego') return `/item/lego/${item._id}`;
      if (itemType === 'movie') return `/item/movie/${item._id}`;
      if (itemType === 'music') return `/item/music/${item._id}`;
      if (itemType === 'book') return `/item/book/${item._id}`;
      if (itemType === 'board-game') return `/item/board-game/${item._id}`;
      return '#';
    },
    getItemDetails: (item) => {
      if (itemType === 'game') {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
              Priority: {item.priority || 'Medium'} •
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>Played:</Typography>
              {item.played ? (
                <CheckCircleIcon color="success" fontSize="small" />
              ) : (
                <CancelIcon color="error" fontSize="small" />
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>Completed:</Typography>
              {item.completed ? (
                <EmojiEventsIcon sx={{ color: 'gold' }} fontSize="small" />
              ) : (
                <CancelIcon color="error" fontSize="small" />
              )}
            </Box>
          </Box>
        );
      }
      if (itemType === 'console') {
        return `Condition: ${item.condition || 'N/A'} • Alternative Name: ${item.alternative_name || 'N/A'}`;
      }
      if (itemType === 'lego') {
        return `Year: ${item.year || 'N/A'} • Theme: ${item.theme || 'N/A'} • Condition: ${item.condition || 'N/A'}`;
      }
      if (itemType === 'movie') {
        return `Year: ${item.year || 'N/A'} • Condition: ${item.condition || 'N/A'}`;
      }
      if (itemType === 'music') {
        return `Year: ${item.year || 'N/A'} • Format: ${item.format || 'N/A'} • Condition: ${item.condition || 'N/A'}`;
      }
      if (itemType === 'board-game') {
        return `Year: ${item.year || 'N/A'} • ${item.playingTime ? `${item.playingTime} min` : 'N/A'} • Condition: ${item.condition || 'N/A'}`;
      }
      return '';
    }
  };

  // Merge default render props with custom render props
  const mergedRenderProps = { ...defaultRenderProps, ...renderProps };

  return (
    <Grid container spacing={2}>
      {items.map((item) => (
        <Grid item xs={12} key={item._id}>
          <Card sx={{ display: 'flex', height: '100%' }}>
            {mergedRenderProps.getImageUrl(item) && (
              <Box sx={{ width: 100, display: { xs: 'none', sm: 'block' } }}>
                <img
                  src={getSecureImageUrl(mergedRenderProps.getImageUrl(item))}
                  alt={mergedRenderProps.getItemName(item)}
                  style={{ 
                    width: '100%', 
                    height: '100%', 
                    objectFit: 'contain',
                    cursor: 'pointer'
                  }}
                  onClick={() => navigate(mergedRenderProps.getItemDetailUrl(item), { 
                    state: { 
                      item,
                      fromWishlist: isWishlist 
                    } 
                  })}
                  onError={handleImageError}
                />
              </Box>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
              <CardContent sx={{ flex: '1 0 auto' }}>
                <Typography 
                  variant="h5" 
                  component="div" 
                  gutterBottom
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(mergedRenderProps.getItemDetailUrl(item), { 
                    state: { 
                      item,
                      fromWishlist: isWishlist 
                    } 
                  })}
                >
                  {mergedRenderProps.getItemName(item)}
                </Typography>
                <Typography variant="subtitle1" color="text.secondary" component="div">
                  {mergedRenderProps.getItemSubtitle(item)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {mergedRenderProps.getItemDetails(item)}
                </Typography>
              </CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', pl: 1, pb: 1 }}>
                {onToggleBacklog && !isWishlist && (
                  <Tooltip title={item.inBacklog ? 'Remove from Backlog' : 'Add to Backlog'}>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); onToggleBacklog(item); }}>
                      {item.inBacklog
                        ? <BookmarkAddedIcon fontSize="small" sx={{ color: '#ce93d8' }} />
                        : <BookmarkAddIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                )}
                {onToggleDNF && !isWishlist && featureFlags.enableDNF === true && (
                  <Tooltip title={item.isDNF ? 'Clear DNF' : 'Mark as DNF'}>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); onToggleDNF(item); }}>
                      <DoNotDisturbOnIcon fontSize="small" sx={{ color: item.isDNF ? 'warning.main' : 'text.disabled' }} />
                    </IconButton>
                  </Tooltip>
                )}
                <IconButton onClick={() => onEdit(item)}>
                  <EditIcon />
                </IconButton>
                <IconButton onClick={() => onDelete(item)} color="error">
                  <DeleteIcon />
                </IconButton>
                {isWishlist && onMoveToCollection && (
                  <IconButton onClick={() => onMoveToCollection(item)} color="primary">
                    <AddShoppingCartIcon />
                  </IconButton>
                )}
              </Box>
            </Box>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

export default LargeView;
